import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';

export function HeartCatcher({ user, profile, onNavigate }) {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [scoreSaved, setScoreSaved] = useState(false);
  const [restartTrigger, setRestartTrigger] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef(null);
  const gameRef = useRef({});

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!gameOver) return;
    setScoreSaved(false);
    supabase.from('heartcatcher_leaderboard').insert([{
      user_id: user.id,
      score: gameRef.current.score,
      username: profile?.username || null,
      email: user.email,
    }]).then(({ error }) => {
      if (error) console.error('Error saving score:', error);
      setScoreSaved(true);
    });
  }, [gameOver, user, profile]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const CATCHER_W = 82;
    const CATCHER_Y = canvas.height - 55;
    const BASE_SPEED = 2.5;
    const CATCHER_SPEED = 13;
    const keys = {};

    gameRef.current = {
      started: false,
      over: false,
      score: 0,
      lives: 3,
      combo: 1,
      time: 0,
      hearts: [],
      particles: [],
      scorePopups: [],
      catcher: { x: canvas.width / 2 },
      flash: 0,
      spawnTimer: 0,
      nextSpawn: 70,
      lastGolden: 0,
      deathFrames: 0,
      animationId: null,
    };

    const game = gameRef.current;
    const ctx = canvas.getContext('2d');

    // Centered heart path, no fill/stroke — caller handles that
    const heartPath = (cx, cy, size) => {
      ctx.beginPath();
      ctx.moveTo(cx,               cy + size * 0.40);
      ctx.bezierCurveTo(cx - size * 0.10, cy + size * 0.20,
                        cx - size * 0.65, cy - size * 0.10,
                        cx - size * 0.65, cy - size * 0.15);
      ctx.bezierCurveTo(cx - size * 0.65, cy - size * 0.55,
                        cx - size * 0.10, cy - size * 0.65,
                        cx,               cy - size * 0.35);
      ctx.bezierCurveTo(cx + size * 0.10, cy - size * 0.65,
                        cx + size * 0.65, cy - size * 0.55,
                        cx + size * 0.65, cy - size * 0.15);
      ctx.bezierCurveTo(cx + size * 0.65, cy - size * 0.10,
                        cx + size * 0.10, cy + size * 0.20,
                        cx,               cy + size * 0.40);
      ctx.closePath();
    };

    const spawnParticles = (x, y, color, count) => {
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.9;
        const speed = 1.6 + Math.random() * 2.6;
        game.particles.push({
          x, y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 1.8,
          color,
          life: 1,
          size: 4 + Math.random() * 5,
        });
      }
    };

    const getSpeedMult = () => 1 + Math.floor(game.time / 1800) * 0.3;
    const getSpawnRate = () => Math.max(36, 70 - Math.floor(game.time / 900) * 4);

    const spawnHeart = () => {
      const t = game.time;
      const speedMult = getSpeedMult();

      let type = 'normal';
      if (t > 0 && t - game.lastGolden >= 900) {
        type = 'golden';
        game.lastGolden = t;
      } else if (t > 3600 && Math.random() < 0.15) {
        type = 'broken';
      } else if (t > 1800 && Math.random() < 0.06) {
        type = 'broken';
      }

      const size = type === 'golden' ? 22 : 14 + Math.random() * 9;
      const vy = (type === 'golden' ? BASE_SPEED * 1.7 : BASE_SPEED + Math.random() * 0.5) * speedMult;
      const palette = ['#f43f5e', '#e879a0', '#ff6b9d', '#c026d3'];

      game.hearts.push({
        x: size + Math.random() * (canvas.width - size * 2),
        y: -size * 0.65,
        vy,
        type,
        size,
        phase: Math.random() * Math.PI * 2,
        color: palette[Math.floor(Math.random() * palette.length)],
        active: true,
      });
    };

    const update = () => {
      if (!game.started || game.over) return;
      game.time++;

      if (keys['ArrowLeft'])  game.catcher.x = Math.max(CATCHER_W / 2, game.catcher.x - CATCHER_SPEED);
      if (keys['ArrowRight']) game.catcher.x = Math.min(canvas.width - CATCHER_W / 2, game.catcher.x + CATCHER_SPEED);

      game.spawnTimer++;
      if (game.spawnTimer >= game.nextSpawn) {
        spawnHeart();
        game.nextSpawn = getSpawnRate();
        game.spawnTimer = 0;
      }

      const cx = game.catcher.x;

      for (const h of game.hearts) {
        if (!h.active) continue;
        h.y += h.vy;
        h.x += Math.sin(h.phase + h.y * 0.016) * 0.45;

        const hBottom = h.y + h.size * 0.40;
        const hTop    = h.y - h.size * 0.65;
        const hLeft   = h.x - h.size * 0.65;
        const hRight  = h.x + h.size * 0.65;

        // Catch
        if (hBottom >= CATCHER_Y - 20 && hTop <= CATCHER_Y + 15 &&
            hRight  >= cx - CATCHER_W / 2 && hLeft <= cx + CATCHER_W / 2) {
          h.active = false;
          if (h.type === 'broken') {
            game.lives = Math.max(0, game.lives - 1);
            game.combo = 1;
            game.flash = 22;
            spawnParticles(h.x, h.y, '#9ca3af', 6);
            if (game.lives === 0) game.over = true;
          } else {
            const pts = (h.type === 'golden' ? 30 : 10) * game.combo;
            game.score += pts;
            game.combo++;
            spawnParticles(h.x, h.y, h.type === 'golden' ? '#FFD700' : h.color, 10);
            game.scorePopups.push({ x: h.x, y: h.y - h.size, text: `+${pts}`, life: 1.2 });
            setScore(game.score);
          }
          continue;
        }

        // Miss
        if (hTop > canvas.height) {
          h.active = false;
          if (h.type !== 'broken') {
            game.lives = Math.max(0, game.lives - 1);
            game.combo = 1;
            game.flash = 22;
            if (game.lives === 0) game.over = true;
          }
        }
      }

      game.hearts = game.hearts.filter(h => h.active);

      for (const p of game.particles) {
        p.x  += p.vx;
        p.y  += p.vy;
        p.vy += 0.08;
        p.life -= 0.034;
        p.size *= 0.97;
      }
      game.particles = game.particles.filter(p => p.life > 0);

      for (const p of game.scorePopups) {
        p.y   -= 1.3;
        p.life -= 0.028;
      }
      game.scorePopups = game.scorePopups.filter(p => p.life > 0);

      if (game.flash > 0) game.flash--;
    };

    const render = () => {
      // Background
      const grad = ctx.createLinearGradient(0, 0, 0, canvas.height);
      grad.addColorStop(0, '#fce4f5');
      grad.addColorStop(1, '#fef0ff');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Life-loss flash
      if (game.flash > 0) {
        ctx.fillStyle = `rgba(200, 30, 30, ${(game.flash / 22) * 0.32})`;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      // Falling hearts
      for (const h of game.hearts) {
        ctx.save();
        if (h.type === 'golden') {
          ctx.fillStyle = '#FFD700';
          ctx.shadowColor = 'rgba(255,215,0,0.7)';
          ctx.shadowBlur = 18;
        } else if (h.type === 'broken') {
          ctx.fillStyle = '#9ca3af';
          ctx.shadowColor = 'rgba(0,0,0,0.15)';
          ctx.shadowBlur = 4;
        } else {
          ctx.fillStyle = h.color;
          ctx.shadowColor = 'rgba(244,63,94,0.3)';
          ctx.shadowBlur = 10;
        }
        heartPath(h.x, h.y, h.size);
        ctx.fill();

        if (h.type === 'broken') {
          ctx.strokeStyle = '#6b7280';
          ctx.lineWidth = 1.5;
          ctx.shadowBlur = 0;
          ctx.beginPath();
          ctx.moveTo(h.x - h.size * 0.05, h.y - h.size * 0.22);
          ctx.lineTo(h.x + h.size * 0.12, h.y + h.size * 0.08);
          ctx.lineTo(h.x - h.size * 0.08, h.y + h.size * 0.30);
          ctx.stroke();
        }

        if (h.type === 'golden') {
          ctx.strokeStyle = 'rgba(255,255,160,0.85)';
          ctx.lineWidth = 1.5;
          ctx.shadowBlur = 0;
          const t = game.time * 0.1;
          for (let i = 0; i < 4; i++) {
            const a = t + (Math.PI / 2) * i;
            ctx.beginPath();
            ctx.moveTo(h.x + Math.cos(a) * h.size * 0.9, h.y + Math.sin(a) * h.size * 0.9);
            ctx.lineTo(h.x + Math.cos(a) * h.size * 1.6, h.y + Math.sin(a) * h.size * 1.6);
            ctx.stroke();
          }
        }
        ctx.restore();
      }

      // Particles (small hearts)
      for (const p of game.particles) {
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.fillStyle = p.color;
        heartPath(p.x, p.y, p.size);
        ctx.fill();
        ctx.restore();
      }

      // Score popups
      for (const p of game.scorePopups) {
        ctx.save();
        ctx.globalAlpha = Math.min(1, p.life);
        ctx.fillStyle = '#111';
        ctx.font = 'bold 14px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(p.text, p.x, p.y);
        ctx.restore();
      }

      // Catcher bowl
      const cx = game.catcher.x;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(cx - CATCHER_W / 2, CATCHER_Y - 18);
      ctx.bezierCurveTo(cx - CATCHER_W / 2, CATCHER_Y + 12,
                        cx - CATCHER_W / 4, CATCHER_Y + 24,
                        cx,                 CATCHER_Y + 24);
      ctx.bezierCurveTo(cx + CATCHER_W / 4, CATCHER_Y + 24,
                        cx + CATCHER_W / 2, CATCHER_Y + 12,
                        cx + CATCHER_W / 2, CATCHER_Y - 18);
      ctx.fillStyle = 'rgba(244, 63, 94, 0.10)';
      ctx.fill();
      ctx.strokeStyle = '#f43f5e';
      ctx.lineWidth = 3.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
      // Heart emblem in basket
      ctx.fillStyle = '#f43f5e';
      ctx.shadowColor = 'rgba(244,63,94,0.45)';
      ctx.shadowBlur = 8;
      heartPath(cx, CATCHER_Y + 5, 9);
      ctx.fill();
      ctx.restore();

      // ── HUD ──────────────────────────────────────────────
      ctx.shadowBlur = 0;

      // Score (top-left)
      ctx.fillStyle = '#111';
      ctx.font = 'bold 22px Inter, system-ui, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(game.score, 16, 38);

      // Combo (top-center, only when > 1)
      if (game.combo > 1) {
        ctx.fillStyle = '#c026d3';
        ctx.font = 'bold 15px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`×${game.combo} COMBO`, canvas.width / 2, 38);
      }

      // Lives (top-right, 3 small hearts)
      for (let i = 0; i < 3; i++) {
        const alive = i < game.lives;
        ctx.fillStyle = alive ? '#f43f5e' : '#e0e0e0';
        ctx.shadowColor = alive ? 'rgba(244,63,94,0.4)' : 'transparent';
        ctx.shadowBlur = alive ? 6 : 0;
        heartPath(canvas.width - 22 - i * 30, 28, 10);
        ctx.fill();
      }
      ctx.shadowBlur = 0;

      // Level indicator (bottom-center, subtle)
      const lvl = Math.floor(game.time / 1800);
      if (lvl > 0) {
        ctx.fillStyle = 'rgba(0,0,0,0.22)';
        ctx.font = '11px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`Level ${lvl + 1}`, canvas.width / 2, canvas.height - 12);
      }

      // Start screen overlay
      if (!game.started) {
        ctx.fillStyle = 'rgba(252, 228, 245, 0.88)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#111';
        ctx.font = 'bold 28px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('💝 Heart Catcher (Beta)', canvas.width / 2, canvas.height / 2 - 52);
        ctx.font = '15px Inter, system-ui, sans-serif';
        ctx.fillStyle = '#555';
        ctx.fillText('Move your mouse or use arrow keys', canvas.width / 2, canvas.height / 2 - 6);
        ctx.fillText('to catch the falling hearts!', canvas.width / 2, canvas.height / 2 + 20);
        ctx.fillStyle = '#9ca3af';
        ctx.font = '13px Inter, system-ui, sans-serif';
        ctx.fillText('Avoid the 💔 broken hearts — they cost a life', canvas.width / 2, canvas.height / 2 + 52);
        ctx.fillStyle = '#f43f5e';
        ctx.font = 'bold 14px Inter, system-ui, sans-serif';
        ctx.fillText('Click or press a key to start', canvas.width / 2, canvas.height / 2 + 86);
      }

      // Death fade
      if (game.over) {
        ctx.fillStyle = `rgba(0,0,0,${Math.min(0.45, game.deathFrames * 0.012)})`;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    };

    const gameLoop = () => {
      update();
      render();
      if (!game.over) {
        game.animationId = requestAnimationFrame(gameLoop);
      } else {
        game.deathFrames++;
        if (game.deathFrames < 38) {
          game.animationId = requestAnimationFrame(gameLoop);
        } else {
          setGameOver(true);
        }
      }
    };

    // ── Controls ─────────────────────────────────────────────
    const handleKeyDown = (e) => {
      if (!game.started) game.started = true;
      keys[e.code] = true;
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.code)) e.preventDefault();
    };
    const handleKeyUp = (e) => { keys[e.code] = false; };

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      game.catcher.x = Math.max(CATCHER_W / 2,
        Math.min(canvas.width - CATCHER_W / 2, (e.clientX - rect.left) * scaleX));
    };

    const handleClick = () => { if (!game.started) game.started = true; };

    const handleTouchStart = (e) => {
      e.preventDefault();
      if (!game.started) game.started = true;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const touch = e.touches[0];
      game.catcher.x = Math.max(CATCHER_W / 2,
        Math.min(canvas.width - CATCHER_W / 2, (touch.clientX - rect.left) * scaleX));
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const touch = e.touches[0];
      game.catcher.x = Math.max(CATCHER_W / 2,
        Math.min(canvas.width - CATCHER_W / 2, (touch.clientX - rect.left) * scaleX));
    };

    render();
    gameLoop();

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('click', handleClick);
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      cancelAnimationFrame(game.animationId);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('click', handleClick);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
    };
  }, [restartTrigger]);

  const handlePlayAgain = () => {
    setScore(0);
    setGameOver(false);
    setRestartTrigger(t => t + 1);
  };

  return (
    <div className="game-container">
      <div className="game-header">
        <div className="settings-menu" ref={settingsRef}>
          <button className="settings-btn" onClick={() => setSettingsOpen(v => !v)} aria-label="Settings">⚙️</button>
          {settingsOpen && (
            <div className="settings-dropdown">
              <div className="settings-email">{user.email}</div>
              <hr className="settings-divider" />
              <button className="settings-item" onClick={() => { setSettingsOpen(false); onNavigate('edit-profile'); }}>Edit Username</button>
              {profile?.is_admin && (
                <button className="settings-item" onClick={() => { setSettingsOpen(false); onNavigate('admin'); }}>Admin Panel</button>
              )}
              <hr className="settings-divider" />
              <button className="settings-item settings-item-danger" onClick={() => supabase.auth.signOut()}>Logout</button>
            </div>
          )}
        </div>
        <span className="game-username">{profile?.username || user.email}</span>
        <button className="btn back-btn" onClick={() => navigate('/')}>← Games</button>
      </div>

      <h1 className="game-title">💝 Heart Catcher (Beta)</h1>
      <canvas ref={canvasRef} width={500} height={600} className="game-canvas" />

      <button className="btn leaderboard-btn" onClick={() => onNavigate('leaderboard')}>
        🏆 Leaderboard
      </button>
      <button className="btn leaderboard-btn" onClick={() => onNavigate('leagues')}>
        🏅 Leagues
      </button>

      {gameOver && (
        <div className="game-over-modal">
          <div className="game-over-box">
            <h2>Game Over!</h2>
            <p className="final-score">{score}</p>
            <button onClick={handlePlayAgain} className="btn">Play Again</button>
            <button onClick={() => onNavigate('leaderboard')} className="btn btn-submit" disabled={!scoreSaved}>
              {scoreSaved ? 'View Leaderboard' : 'Saving…'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
